// DTO shapes returned by NewRosterController (overwatch-api) — kept 1:1 in sync with
// overwatch-api/UrKnightAPI/Models/NewRosterModels.cs.

export interface NewRosterAssignment {
  id: string;
  name: string;
  assignmentCode: string;
  siteCount: number;
  staffCount: number;
}

export interface NewRosterSite {
  id: string;
  name: string;
  eventCode: string;
  address: string;
  assignmentId: string;
}

export type ShiftType = 'morning' | 'day' | 'evening' | 'night' | 'overnight';

export interface ShiftBreak {
  id: string;
  description: string;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
  totalHours: number;
  isCrossesMidnight: boolean;
}

export interface NewRosterShiftTemplate {
  id: string;
  name: string;
  shiftCode: string;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
  color: string;
  shiftType: ShiftType;
  requiredStaff: number;
  breaks: ShiftBreak[];
}

export interface NewRosterWorkgroup {
  id: string;
  name: string;
  color: string;
}

export type StaffAvailability = 'free' | 'busy' | 'partial';

export interface NewRosterStaff {
  id: string;
  name: string;
  initials: string;
  color: string;
  role: string;
  workgroupId: string;
  workgroupName: string;
  status: StaffAvailability;
  busyNote: string;
}

export interface NewRosterBootstrap {
  assignments: NewRosterAssignment[];
  workgroups: NewRosterWorkgroup[];
  staff: NewRosterStaff[];
}

// ─ Wizard-only shapes (never round-trip through the API as-is) ─

export interface RosterShiftInstance {
  id: string;               // client-generated instance id, e.g. "sh-new-<timestamp>"
  sourceTemplateId: string | null;
  name: string;
  shiftCode?: string;
  tStart: string;
  tEnd: string;
  color: string;
  stype: ShiftType;
  reqStaff: number;
  workPattern: number[];     // Mon..Sun, 1 = active, 0 = off
  breaks: ShiftBreak[];
  // true = already persisted (loaded via getRosterDetail, or just saved via saveShiftChanges());
  // false/undefined = added this session and not yet sent to POST /newroster/{rosterId}/shifts.
  isSaved?: boolean;
  // Only meaningful once isSaved — needed to call PUT .../shifts/{shiftSnapshotId} and for the
  // LockVersion compare-and-swap guard.
  shiftSnapshotId?: string;
  lockVersion?: number;
  // Baseline snapshot taken whenever isSaved becomes true — compared against workPattern/reqStaff to
  // detect a dirty (unsaved) edit on an already-saved shift (see isShiftDirty()).
  originalWorkPattern?: number[];
  originalReqStaff?: number;
  // How many active (non-Cancelled) RosterStaffAssignment rows this shift has — from GetDetail. Drives
  // whether deleting it needs a "Delete & Unassign" confirmation (New) or is blocked outright (Revoked).
  activeAssignmentCount?: number;
  // Client-only staging flag — the shift stays visible (dimmed) until Save actually deletes it.
  markedForDeletion?: boolean;
  // Client-only staging flag set by "Sync" on the drift banner — refreshes Name/Time/Breaks from the
  // live Shift template the next time this shift is saved (see EditRosterShiftRequest.syncContentFromTemplate).
  pendingContentSync?: boolean;
  isStandby?: boolean;
  originalIsStandby?: boolean;
}

// ─ Create Roster request/response (POST /newroster) ─

export interface CreateRosterShiftRequest {
  clientShiftId: string;
  sourceTemplateId: string | null;
  name: string;
  startTime: string;
  endTime: string;
  color: string;
  shiftType: ShiftType;
  requiredStaff: number;
  workPattern: boolean[];
  isStandby?: boolean;
}

// Mode of an enabled device. A device that is switched off behaves like "Off" (see StaffMovementDeviceSetting.enabled).
export type StaffMovementDisplayMode = 'Realtime' | 'Interval';

// Mobile platforms that can be individually enabled for Staff Movement Display. Keys match
// Capacitor.getPlatform(). To support a new mobile OS, add it here (and to Constants.MobilePlatform /
// StaffMovementPlatforms.Supported on the backend).
export const STAFF_MOVEMENT_PLATFORM_OPTIONS: { key: string; label: string; icon: string; description: string }[] = [
  { key: 'android', label: 'Android', icon: 'logo-android', description: 'Android phones & tablets' },
  { key: 'ios', label: 'iOS', icon: 'logo-apple', description: 'iPhone & iPad' },
];

// 'All' (every mobile platform, including ones added later) or a CSV of platform keys, e.g. 'android,ios'.
// Only used by the staff app, which still receives the flat value resolved for its own platform.
export const STAFF_MOVEMENT_PLATFORMS_ALL = 'All';

// Inactivity Warning Interval (minutes) — see Constants.InactivityWarning on the backend. The backend job
// (StopDutyHostedService) runs every 15 minutes, which is why the minimum is 15.
export const INACTIVITY_WARNING_MIN = 15;
export const INACTIVITY_WARNING_MAX = 240;
export const INACTIVITY_WARNING_DEFAULT = 60;

// Distance (meters) bounds / default of the Interval mode (see NewRosterController validation).
export const STAFF_MOVEMENT_INTERVAL_MIN = 10;
export const STAFF_MOVEMENT_INTERVAL_MAX = 200;
export const STAFF_MOVEMENT_INTERVAL_DEFAULT = 30;

// Staff Movement Display configuration of ONE device — the device is the top-level unit: tracking can be
// switched on/off per device, and the mode and the Inactivity Warning are properties of that device.
export interface StaffMovementDeviceSetting {
  enabled: boolean;
  mode: StaffMovementDisplayMode;
  intervalMeters: number | null;
  inactivityEnabled: boolean;
  inactivityMinutes: number | null;
}

// Keyed by device key (STAFF_MOVEMENT_PLATFORM_OPTIONS[].key: 'android' | 'ios').
export type StaffMovementDevices = Record<string, StaffMovementDeviceSetting>;

export function defaultStaffMovementDevice(): StaffMovementDeviceSetting {
  return {
    enabled: true,
    mode: 'Realtime',
    intervalMeters: null,
    inactivityEnabled: true,
    inactivityMinutes: INACTIVITY_WARNING_DEFAULT,
  };
}

// Every device enabled, Realtime, Inactivity Warning ON at 60 minutes.
export function defaultStaffMovementDevices(): StaffMovementDevices {
  return Object.fromEntries(STAFF_MOVEMENT_PLATFORM_OPTIONS.map((o) => [o.key, defaultStaffMovementDevice()]));
}

export interface CreateRosterRequest {
  name: string;
  colorCode: string;
  startDate: string; // "YYYY-MM-DD"
  endDate: string;   // "YYYY-MM-DD"
  notifyOutsideGeofence: boolean;
  staffMovementDevices: StaffMovementDevices;
  assignmentId: string;
  siteId: string;
  shifts: CreateRosterShiftRequest[];
  tagIds: string[];
}

export interface CreateRosterShiftSnapshot {
  clientShiftId: string;
  shiftId: string;
  shiftSnapshotId: string;
  lockVersion: number;
}

export interface CreateRosterResponse {
  id: string;
  name: string;
  createdAtUtc: string;
  rosterSnapshotId: string;
  status: string;
  shiftSnapshots: CreateRosterShiftSnapshot[];
}

// ─ Reload an existing roster (F5-safety / edit) — GET /newroster/{id} ─
// Everything here comes from the roster's snapshot rows on the backend, not live master data.

export interface NewRosterShiftBreak {
  description: string;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
}

export interface NewRosterDetailShift {
  shiftId: string;
  shiftSnapshotId: string;
  lockVersion: number;
  name: string;
  shiftCode: string;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
  isCrossesMidnight: boolean;
  breaks: NewRosterShiftBreak[];
  requiredStaff: number | null;
  weekdays: number[]; // Sun=0..Sat=6
  activeAssignmentCount: number;
  isStandby?: boolean;
}

export interface NewRosterDetail {
  id: string;
  name: string;
  colorCode: string;
  startDate: string; // "YYYY-MM-DD"
  endDate: string;   // "YYYY-MM-DD"
  notifyOutsideGeofence: boolean;
  staffMovementDevices: StaffMovementDevices;
  rosterSnapshotId: string;
  timeZoneId: string | null;
  assignment: NewRosterAssignment;
  site: NewRosterSite;
  shifts: NewRosterDetailShift[];
  staffAssignmentTotal: number | null;
  staffAssignmentCompleted: number | null;
  status: string;
  minAssignedDate: string | null; // "YYYY-MM-DD"
  maxAssignedDate: string | null; // "YYYY-MM-DD"
}

// ─ Add a new shift to a roster that already exists — POST /newroster/{rosterId}/shifts ─

export interface AddRosterShiftRequest {
  sourceTemplateId: string;
  workPattern: boolean[];
  requiredStaff: number;
  reason?: string;
  isStandby?: boolean;
}

// ─ Edit an already-saved shift's pattern/required-staff in place — PUT /newroster/{rosterId}/shifts/{shiftSnapshotId} ─

export interface EditRosterShiftRequest {
  workPattern: boolean[];
  requiredStaff: number;
  lockVersion: number;
  reason?: string;
  syncContentFromTemplate?: boolean;
  isStandby?: boolean;
}

// ─ Roster lifecycle — POST /newroster/{rosterId}/publish and /revoke ─

export interface RosterStatusResponse {
  status: string;
}

// ─ Edit Start/End Date, Assignment and Site — PUT /newroster/{rosterId}/details ─

export interface EditRosterDetailsRequest {
  name: string;
  colorCode: string;
  startDate: string; // "YYYY-MM-DD"
  endDate: string;   // "YYYY-MM-DD"
  assignmentId: string;
  siteId: string;
  notifyOutsideGeofence: boolean;
  staffMovementDevices: StaffMovementDevices;
  reason?: string;
  syncAssignment?: boolean;
  syncSite?: boolean;
}

// ─ Delete a shift (cascade-cancels active assignments) — DELETE /newroster/{rosterId}/shifts/{shiftSnapshotId} ─

export interface DeleteShiftResponse {
  shiftSnapshotId: string;
  unassignedCount: number;
}

// ─ Master-data drift (Assignment/Site/Shift) vs the roster's snapshot — GET /newroster/{rosterId}/drift ─
// Only meaningful while roster Status is New.

export interface AssignmentDrift {
  isDeleted: boolean;
  snapshotName: string;
  liveName: string | null;
  nameChanged: boolean;
  snapshotCode: string | null;
  liveCode: string | null;
  codeChanged: boolean;
}

export interface SiteDrift {
  isDeleted: boolean;
  snapshotName: string;
  liveName: string | null;
  nameChanged: boolean;
  snapshotCode: string | null;
  liveCode: string | null;
  codeChanged: boolean;
  snapshotLocation: string | null;
  liveLocation: string | null;
  locationChanged: boolean;
  snapshotTimeZoneId: string | null;
  liveTimeZoneId: string | null;
  timeZoneChanged: boolean;
  geofenceChanged: boolean;
  buildingSpecChanged: boolean;
  beaconDeviceSpecChanged: boolean;
}

export interface ShiftDrift {
  shiftSnapshotId: string;
  isDeleted: boolean;
  snapshotName: string;
  liveName: string | null;
  nameChanged: boolean;
  snapshotCode: string | null;
  liveCode: string | null;
  codeChanged: boolean;
  snapshotTime: string | null;
  liveTime: string | null;
  timeChanged: boolean;
  snapshotBreakTime: string | null;
  liveBreakTime: string | null;
  breakChanged: boolean;
  canSync: boolean;
}

export interface RosterDrift {
  hasDrift: boolean;
  assignment: AssignmentDrift | null;
  site: SiteDrift | null;
  shifts: ShiftDrift[];
}

// ─ Excel Import — POST /newroster/import/preview and /import/apply ─
// When rosterId is supplied, Assignment/Site are fixed by that roster and only ever validated (mismatch
// = row error). When rosterId is omitted, Import can create a brand-new roster — Assignment/Site/Shift
// may all need creation (see NewRosterController.ImportPreview/ImportApply).

export interface ImportResolvedEntity {
  code: string | null; // null when the source (clone snapshot) had no code
  id: string | null;   // null = not matched, needs resolving (pick existing or create)
  name: string | null;
}

// Built for every distinct shift_code in the file (or every shift in a cloned roster), matched or not —
// id set = resolved (shown as "Resolved" with the live Shift's own name/start/end), null = needs
// resolving. shiftCode is null only for a cloned shift whose source snapshot had no code.
export interface ImportShiftSummary {
  shiftCode: string | null;
  shiftName: string | null;
  shiftStart: string | null;
  shiftEnd: string | null;
  requiredStaff: number | null;
  id: string | null;
  staffCount: number;
  // Client-only. Set the first time the admin resolves this entry (pick existing / create) to the code
  // it had before — drives the "OLD → NEW" hint in the header and the "Change" affordance. `undefined`
  // means the entry was auto-matched by the backend and was never touched by the admin.
  sourceShiftCode?: string | null;
}

export interface ImportRowError {
  rowNumber: number;
  message: string;
}

// An unrecognized staff_code doesn't block the file — the row is just excluded, reported here so the
// dialog can tell the admin which staff will be skipped.
export interface ImportSkippedStaff {
  rowNumber: number;
  staffCode: string;
}

export interface ImportRow {
  rowNumber: number;
  shiftCode: string;
  requiredStaff: number | null;
  staffCode: string;
  staffId?: string | null; // set on the clone path (staff sourced by id, may have no staffCode)
  staffName: string;
  isLeader: boolean;
  workingDate: string; // "YYYY-MM-DD"
}

export interface ImportPreviewResponse {
  rosterName: string | null;    // from the file's first data row — only relevant when rosterId is absent
  startDate: string | null;     // "YYYY-MM-DD"
  endDate: string | null;
  assignment: ImportResolvedEntity | null;
  site: ImportResolvedEntity | null;
  shifts: ImportShiftSummary[];
  rowErrors: ImportRowError[];
  skippedStaff: ImportSkippedStaff[];
  rows: ImportRow[];
}

export interface ImportApplyRow {
  shiftCode: string;
  requiredStaff: number | null;
  staffCode: string;
  staffId?: string | null; // clone path only — lets ImportApply resolve staff without a staffCode
  isLeader: boolean;
  workingDate: string; // "YYYY-MM-DD"
}

// Every shift the admin resolved (matched or quick-created) in the dialog — independent of rows, so a
// shift with zero staff rows still gets attached to the roster instead of being silently dropped.
export interface ImportApplyShift {
  shiftCode: string;
  requiredStaff: number | null;
}

export interface ImportApplyRequest {
  rosterId: string | null;      // null = create a new roster
  rosterName: string | null;    // only used when rosterId is null
  colorCode: string | null;     // only used when rosterId is null
  startDate: string | null;     // "YYYY-MM-DD"
  endDate: string | null;
  assignmentId: string | null;  // resolved (existing or just-created)
  siteId: string | null;
  shifts: ImportApplyShift[];
  rows: ImportApplyRow[];
}

export interface ImportApplyResponse {
  rosterId: string;   // the roster used (or just created) — frontend navigates here
  importedCount: number;
}

// ─ Clone (roster.page.html Archived tab) — GET /newroster/{rosterId}/clone-source ─
// Feeds ImportPreviewDialogComponent in 'clone' mode: no dates, no rows — the dialog expands rows
// client-side from each staff's own historical weekday pattern once the admin picks Start/End Date.

export interface CloneSourceStaff {
  staffId: string;
  staffCode: string; // may be empty — historical staff need not have a UserProfile.StaffCode
  staffName: string;
  isLeader: boolean;
  weekdays: number[]; // 0=Sun..6=Sat, distinct — this staff's own historical pattern for this shift
}

export interface CloneSourceShift {
  shiftCode: string | null; // null when the source snapshot had no code
  shiftName: string | null;
  shiftStart: string | null;
  shiftEnd: string | null;
  requiredStaff: number | null;
  id: string | null;
  staff: CloneSourceStaff[];
}

export interface CloneSourceResponse {
  rosterName: string;
  colorCode: string | null;
  assignment: ImportResolvedEntity | null;
  site: ImportResolvedEntity | null;
  shifts: CloneSourceShift[];
}

// ─ Excel Import (Phase 2a staging) — GET /newroster/{rosterId}/import/staging and
// POST /newroster/{rosterId}/import/staging/complete ─

export interface ImportStagingStaff {
  staffId: string;
  staffCode: string;
  staffName: string;
  isLeader: boolean;
  workingDates: string[]; // "YYYY-MM-DD"[]
  // Dates where the admin already made a Force Assign / UnAssign decision on Conflict Check, persisted
  // server-side — lets the page restore that state on any fresh load (refresh, or a different machine).
  forcedDates?: string[];
  excludedDates?: string[];
}

export interface ImportStagingShift {
  shiftCode: string;
  shiftSnapshotId: string;
  lockVersion: number;
  shiftName: string;
  startTime: string; // "HH:mm"
  endTime: string;
  requiredStaff: number | null;
  staff: ImportStagingStaff[];
}

export interface ImportStagingResponse {
  shifts: ImportStagingShift[];
}

export interface ImportStagingRowKey {
  shiftCode: string;
  staffCode: string;
  staffId?: string | null;
  workingDate: string; // "YYYY-MM-DD"
}

export interface ImportStagingCompleteRequest {
  excludedRows: ImportStagingRowKey[];
}

export interface ImportStagingCompleteResponse {
  remainingCount: number;
}
